import React from "react";
import { type NextPage } from "next";
import { Page } from "components/page";
import { FillImage } from "components/images";
import { CenteredQuote } from "components/quotes";
import { Customers } from "components/logos";
import { Section, sectionSpacingY } from "components/section";
import { NavatticDemoModal } from "components/demo-embed";
import { BookDemoButton } from "components/event-tracking";
import { implementationEffortComparison } from "components/content/traditional-cdp-vs-hightouch/data";
import {
  CustomerStats,
  CustomerWithStatsSlug,
  DestinationMarquee,
  IndustryLeaderAwards,
  SecurityFeatures,
  TraditionalCdpVsHightouch,
} from "components/content";
import {
  desktopOnlyDisplay,
  getLandingPageProps,
  getPageMetaImage,
  getSafeCustomer,
  mobileOnlyDisplay,
  type LandingPageProps,
} from "utils";
import { AuraPlate } from "ui/plates";
import { TextTitle } from "ui/text";
import { ProductShowcase, ProductShowcaseImage } from "ui/product-showcase";
import { Diptych } from "ui/diptych";
import { Button } from "ui/button";
import { HealthcareHero } from "components/solutions/healthcare/hero";
import productScreen from "../../../public/assets/solutions/healthcare/product-screen.png";
import mobileProductScreen from "../../../public/assets/solutions/healthcare/mobile-product-screen.png";
import firstDiptychImage from "../../../public/assets/shared-illustrations/diptych-1.png";
import secondDiptychImage from "../../../public/assets/shared-illustrations/diptych-2.png";
import thirdDiptychImage from "../../../public/assets/shared-illustrations/diptych-3.png";

const Healthcare: NextPage<LandingPageProps> = ({ page, announcementBanner, customers, destinations }) => {
  const weightWatchers = getSafeCustomer(customers, "weight-watchers");
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      theme="dark"
      bgColor="new.green.950"
    >
      <HealthcareHero eyebrow={page.eyebrow} title={page.title} description={page.description} />

      {page.customers && (
        <Customers
          customers={page.customers}
          title="Trusted by leading healthcare companies"
          my={0}
          pt={sectionSpacingY}
          pb={{ base: 0, lg: "small" }}
        />
      )}

      <CustomerStats
        customers={customers}
        title="Drive better patient, provider, and business outcomes with your data"
      />

      <AuraPlate condensed variant="gray">
        <CenteredQuote
          quoteText="Hightouch gives us flexibility—one platform to manage all of our customer data use cases. Our marketing team has instant access to any data point living in our warehouse, and we can sync audiences immediately to any of our marketing applications. It just works."
          author={{ name: "David Cohen", job: "Chief Data Officer" }}
          company={{ name: "Weight Watchers", logo: weightWatchers.logoUrl }}
          maxW="940px"
        />
      </AuraPlate>

      <Section>
        <TextTitle
          display={mobileOnlyDisplay("flex")}
          size="lg"
          maxW="600px"
          title="Activate marketing in a BAA-supported platform"
          description="Talk to a product expert and learn how you can sync customer data to any business tool."
          actions={<BookDemoButton />}
        />

        <TextTitle
          display={desktopOnlyDisplay("flex")}
          align="center"
          size="lg"
          maxW="600px"
          title="Activate marketing in a BAA-supported platform"
          description="Check out this 5 minute interactive demo to learn how you can sync customer data to any business tool."
          actions={<NavatticDemoModal hasRightChevron>Start Exploring</NavatticDemoModal>}
        />

        <ProductShowcase
          hasBorder
          mt={{ base: "big", md: "biggest" }}
          variant="mustard"
          productScreen={<ProductShowcaseImage src={productScreen} alt="Hightouch Audiences product UI." />}
          mobileProductScreen={
            <ProductShowcaseImage isMobile src={mobileProductScreen} alt="Hightouch Audiences product UI." />
          }
        />
      </Section>

      <TraditionalCdpVsHightouch
        titleMaxW="660px"
        title="The Best CDP for Healthcare"
        description="Don’t put yourself through an expensive and lengthy implementation with traditional CDPs. Use your existing data to maximize business goals and patient outcomes across all of your tools."
        rows={[
          {
            category: "Data storage",
            traditionalCdp: {
              title: "Copies data outside of your infrastructure",
              description:
                "Traditional CDPs are built on duplicative data storage — your database and theirs. This increases your data governance risks and creates hurdles for HIPAA-compliance.",
            },
            hightouch: {
              title: "Doesn’t store data and offers robust governance",
              description:
                "Hightouch operates within your data infrastructure, vastly simplifying HIPAA-compliance and data governance. You have granular control over how data can and cannot be used.",
            },
          },
          {
            category: "Data source flexibility",
            traditionalCdp: {
              title: "Operates on a narrow silo of customer behaviors",
              description:
                "Traditional CDPs were built to handle user and behavioral event data from websites and mobile apps. It’s expensive to integrate data from your other systems, or organize it into non-user entities like insurance policies.",
            },
            hightouch: {
              title: "Takes action on any permitted data source",
              description:
                "Your data warehouse can ingest, organize, and govern data from any of your systems, for any entities that matter to your business and customers. Hightouch enables you to act on your complete data.",
            },
          },
          implementationEffortComparison,
        ]}
      />

      <SecurityFeatures titleAs="h3" />

      <Section>
        <Diptych
          media={
            <FillImage
              src={firstDiptychImage}
              alt="Abstract illustration of data flowing from top-to-bottom between three rows. The first row is labeled Data Warehouse with logos for Snowflake, Databricks, and Google BigQuery. The second row is labeled cloud bucket. The third is labeled destinations with logos for Facebook, HubSpot, and TikTok."
            />
          }
          direction="right"
          title="Keep data in your infrastructure"
          description="Hightouch never stores any of your data. We believe you should maintain control and take advantage of your existing architecture. We operate directly in your data warehouse."
          actions={
            <Button hasRightChevron href="/docs/security/overview#basic-architecture-overview" target="_blank">
              See our product architecture
            </Button>
          }
        />
      </Section>

      <Section>
        <Diptych
          media={
            <FillImage
              src={secondDiptychImage}
              alt="Abstract illustration representing the secure relationship between enterprise teams like Data, Marketing, and Operations teams with Hightouch resources like Audiences, Models, and Syncs."
            />
          }
          direction="left"
          title="Enterprise-ready on day one"
          description="Hightouch provides the control and governance capabilities you need to give your marketers safe self-service access."
          actions={
            <Button hasRightChevron href="/blog/enterprise-ready-on-day-1" target="_blank">
              See our governance features
            </Button>
          }
        />
      </Section>

      <Section my={0} pb={sectionSpacingY}>
        <Diptych
          media={
            <FillImage
              src={thirdDiptychImage}
              alt="Abstract illustration highlighting SOC 2 Type 2, AES-256, HIPAA, and GDPR security standards."
            />
          }
          direction="right"
          title="Product security"
          description="Our platform architecture and industry compliance certifications mean that your data is always secure."
          actions={
            <Button hasRightChevron href="/docs/security/overview" target="_blank">
              Learn about data security
            </Button>
          }
        />
      </Section>

      <IndustryLeaderAwards />

      <DestinationMarquee destinations={destinations} my={0} py={sectionSpacingY} />
    </Page>
  );
};

export const getStaticProps = getLandingPageProps("/solutions/healthcare", {
  loadDestinations: "all",
  loadCustomers: [
    CustomerWithStatsSlug.HEALTHCARE_STAFFING_COMPANY,
    CustomerWithStatsSlug.WEIGHT_WATCHERS,
    CustomerWithStatsSlug.WHOOP,
  ],
});

export default Healthcare;
